/**
 * fMP4 duration patching.
 *
 * The sibling of {@link ./webm-duration.helper.ts}, for the other container and for exactly the same
 * reason. A clip cut from an HLS stream is assembled by gluing hls.js' init segment (`ftyp` + `moov`)
 * to the `moof`/`mdat` fragments it transmuxed. That `moov` was built to describe a *live, unbounded*
 * stream, so `mvhd`/`tkhd`/`mdhd` all carry `duration = 0` and the real length only exists implicitly,
 * spread across the fragment headers.
 *
 * A player fed that file reports `video.duration === 0` (or `Infinity` once it starts probing
 * fragments): no progress bar, no seeking, and the bench has to stream the whole clip before it will
 * let the user navigate — over the LAN or from the presigned cloud URL. Writing the real duration into
 * those three boxes fixes it.
 *
 * The patch is deliberately *in place*: every field we touch already exists and keeps its width, so no
 * box size changes and no parent offset has to be repaired. That is what makes this safe compared to
 * the WebM case, where inserting `Duration` grows `Info` and drags `SeekHead`/`Cues` out of alignment.
 *
 * Everything degrades silently: if the container is not shaped the way we expect we return the original
 * blob untouched, because a clip without a duration is still far better than a corrupt one.
 */

import { readUint32, walkBoxes, writeUint32 } from './mp4-boxes.helper';

/** Boxes we descend into looking for the three headers. `moov > trak > mdia` is the whole path. */
const CONTAINER_BOXES = new Set(['moov', 'trak', 'mdia']);

/** Cheap sniff of the `ftyp` box every MP4 opens with, so we never touch a WebM. */
export const looksLikeMp4 = (head: Uint8Array): boolean =>
  head.length >= 8 && head[4] === 0x66 && head[5] === 0x74 && head[6] === 0x79 && head[7] === 0x70;

/**
 * Write a duration that may not fit in 32 bits. Version-1 boxes store it as a 64-bit integer; we only
 * ever produce clips of at most a minute, so the high word is always zero, but it still has to be
 * cleared in case the source `moov` left something there.
 */
const writeDuration = (buf: Uint8Array, offset: number, version: number, value: number): void => {
  if (version === 1) {
    writeUint32(buf, offset, 0);
    writeUint32(buf, offset + 4, value);
  } else {
    writeUint32(buf, offset, value);
  }
};

/**
 * Offsets of `timescale` and `duration` inside a full box, relative to the end of its 4-byte
 * version+flags header. `tkhd` has no timescale of its own — it is expressed in the movie timescale
 * from `mvhd`, which is why it reports `timescale: -1` here.
 */
const FIELD_LAYOUT: Record<string, (version: number) => { timescale: number, duration: number }> = {
  // creation(4|8) modification(4|8) timescale(4) duration(4|8)
  mvhd: version => version === 1 ? { timescale: 16, duration: 20 } : { timescale: 8, duration: 12 },
  mdhd: version => version === 1 ? { timescale: 16, duration: 20 } : { timescale: 8, duration: 12 },
  // creation(4|8) modification(4|8) track_ID(4) reserved(4) duration(4|8)
  tkhd: version => version === 1 ? { timescale: -1, duration: 24 } : { timescale: -1, duration: 16 },
};

/**
 * Write `durationMs` into every `mvhd`, `tkhd` and `mdhd` of an fMP4, each in its own timescale.
 * Returns a new blob, or the original one untouched when the file is not shaped as expected.
 */
export const patchMp4Duration = async (blob: Blob, durationMs: number): Promise<Blob> => {
  try {
    if (!(durationMs > 0)) return blob;
    const buf = new Uint8Array(await blob.arrayBuffer());
    if (!looksLikeMp4(buf)) return blob;

    // The movie timescale governs both `mvhd` and every `tkhd`, and it is always declared before them,
    // so a single forward pass is enough — no need to walk the tree twice.
    let movieTimescale = 0;
    let patched = 0;

    const parsed = walkBoxes(buf, 0, buf.length, CONTAINER_BOXES, box => {
      const layout = FIELD_LAYOUT[box.type];
      if (!layout) return;

      const version = buf[box.contentStart];
      const fields = layout(version);
      const base = box.contentStart + 4; // past version(1) + flags(3)

      const timescale = fields.timescale >= 0 ? readUint32(buf, base + fields.timescale) : movieTimescale;
      if (box.type === 'mvhd') movieTimescale = timescale;
      if (!timescale) return;

      const durationOffset = base + fields.duration;
      const width = version === 1 ? 8 : 4;
      if (durationOffset + width > box.end) return;

      writeDuration(buf, durationOffset, version, Math.round((durationMs * timescale) / 1000));
      patched++;
    });

    if (!parsed || !patched) return blob;
    return new Blob([buf], { type: blob.type || 'video/mp4' });
  } catch (error) {
    console.warn('Could not patch MP4 duration, keeping the original blob', error);
    return blob; // never break a recording over a patching failure
  }
};

/**
 * Make a freshly assembled clip seekable.
 *
 * `durationMs` is measured from the fragment timeline rather than a wall clock, because that is what
 * the file actually contains: whole HLS segments were kept or dropped, so the media is exactly as long
 * as the fragments that survived, regardless of how long the user held the button.
 *
 * Non-MP4 input is returned untouched, mirroring `ensureSeekableWebM`, so the two recording paths can
 * both call their own helper without having to know which container the other one produces.
 */
export const ensureSeekableMp4 = async (blob: Blob, durationMs: number): Promise<Blob> => {
  try {
    if (!blob?.size) return blob;
    if (!looksLikeMp4(new Uint8Array(await blob.slice(0, 8).arrayBuffer()))) return blob;
    return await patchMp4Duration(blob, durationMs);
  } catch (error) {
    console.warn('Could not make the clip seekable, keeping the original blob', error);
    return blob;
  }
};
